import React, { useCallback, useEffect, useState } from "react";

import { ArrowForward, Search } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Button,
} from "@mui/material";
import debounce from "lodash.debounce";
import { enqueueSnackbar } from "notistack";
import { connect } from "react-redux";

// import AssessmentDetailsModal from "./AssessmentDetailModal";
import AssessmentsCards from "./AssessmentsCards";
import RecentlyCreatedAssessments from "./RecentlyCreatedAssessments";
import AIROBOT from "../../assets/images/robot.png";
import { GridListView } from "../../common";
import {
  selectLoading,
  getSingleDepartmentAssessment as getSingleDepartmentAssessmentAction,
  getAllAssessments as getAllAssessmentsAction,
  getFilterData as getFilterDataAction,
  selectPage,
  selectAssessmentIds,
  applyAndSearchFilter as applyAndSearchFilterAction,
} from "../../redux/slices/assessmentsSlice";
import { setActionButtonToggle as setActionButtonToggleAction } from "../../redux/slices/authSlice";
import { getCamelCaseWord } from "../../utils/functions";
import AssessmentDetailsModal from "../Modals/AssessmentDetailModal";
import InviteViaAssessmentModal from "../Modals/InviteViaAssessmentModal";

const InterviewAssessments = ({
  getAllAssessments,
  getFilterData,
  assessments,
  setActionButtonToggle,
  page,
  isModal,
  onSelect,
  onClose,
  company,
  filters,
  isUserCompanySetup,
}) => {
  const [expanded, setExpanded] = useState([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState({}); // To track selected options
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [interviewValue, setInterviewValue] = useState(77);
  const [passingCriteriaSet, setPassingCriteriaSet] = useState(false);
  const [tabValue, setTabValue] = useState("share_link");
  const [copied, setCopied] = useState(false);
  const [linkGenerated, setLinkGenerated] = useState(false);
  const [interviewId, setInterviewId] = useState("");
  const departmentIds = company?.departments?.map((dept) => dept?.departmentId);

  const handleClose = () => {
    setOpenDetailModal(false);
    setInterviewId("");
    setLinkGenerated(false);
    setPassingCriteriaSet(false);
    setTabValue("share_link");
    setInterviewValue(77);
  };

  useEffect(() => {
    let isMounted = true; // Prevent state updates after unmount

    const fetchData = async () => {
      try {
        await _getData(departmentIds || [], 0, selectedFilters);

        if (!company) {
          setIsLoading(false);
          setActionButtonToggle(true);
          console.log("No company data, possibly logged out.");
          return;
        }

        if (!isUserCompanySetup) {
          setIsLoading(false);
          setActionButtonToggle(true);
          if (isMounted) {
            console.log("No company data, possibly company is not setup.");
          }
          return;
        }

        await getFilterData();
      } catch (error) {
        console.error("Error in getting assessments of departments", error);
      }
    };

    fetchData();

    return () => {
      isMounted = false; // Cleanup to prevent state updates after unmount
    };
  }, [company, isUserCompanySetup]);

  const _getData = useCallback(
    async (departmentIds, _page, filters, hideLoader) => {
      if (!hideLoader) {
        setIsLoading(true);
      }

      console.log("Filters applied in get data function", filters);

      await getAllAssessments({
        departmentIds,
        page: _page + 1,
        filter: filters,
      });

      setIsLoading(false);
    },
    [] // Removed unnecessary dependencies to avoid rerenders
  );

  const debounceApplyFilters = useCallback(
    debounce(async (updatedFilters) => {
      setIsLoading(true);

      const departmentIds = company?.departments?.map(
        (dept) => dept?.departmentId
      );
      await _getData(departmentIds, 0, updatedFilters);

      setIsLoading(false);
    }, 1000), // Adjust debounce time if needed
    [_getData]
  );

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    debounceApplyFilters({ ...selectedFilters, query: event.target.value });
  };

  const handleAccordionToggle = (panel) => {
    setExpanded((prevExpanded) =>
      prevExpanded.includes(panel)
        ? prevExpanded.filter((item) => item !== panel)
        : [...prevExpanded, panel]
    );
  };

  const handleAssessmentSelect = (assessment) => {
    if (!isUserCompanySetup) {
      setActionButtonToggle(true);
      enqueueSnackbar("Please setup company!", { variant: "info" });
      return;
    }

    if (selectedAssessmentId === assessment?.id) {
      setSelectedAssessmentId(null);
    } else {
      if (onSelect) {
        if (onClose) onClose();
        onSelect(assessment?.id);
      }
      setSelectedAssessmentId(assessment?.id);
    }
  };

  const handleOpenModal = (assessment) => {
    setSelectedAssessment(assessment);
    setOpenModal(true);
  };

  useEffect(() => {
    if (Object.keys(selectedFilters).length > 0 || searchQuery) {
      debounceApplyFilters({
        ...selectedFilters,
        query: searchQuery,
      });
    }
    return debounceApplyFilters.cancel;
  }, [selectedFilters, searchQuery, debounceApplyFilters]);

  const handleCheckboxChange = (category, option) => {
    setSelectedFilters((prev) => {
      const currentCategory = prev[category] || [];
      const updatedCategory = currentCategory.includes(option)
        ? currentCategory.filter((item) => item !== option)
        : [...currentCategory, option];

      const updatedFilters = {
        ...prev,
        [category]: updatedCategory,
      };

      return updatedFilters;
    });
  };

  const renderHeader = useCallback(() => {
    return (
      <RecentlyCreatedAssessments
        handleAssessmentSelect={handleAssessmentSelect}
        handleOpenModal={handleOpenModal}
        isModal={isModal}
        searchQuery={searchQuery}
        selectedFilters={selectedFilters}
        selectedAssessmentId={selectedAssessmentId}
      />
    );
  }, [isModal, searchQuery, selectedFilters, selectedAssessmentId]);

  const sortFilters = (filters) => {
    const sortedFilters = { ...filters };

    // Define the expected/preferred order for common categories
    const orderMappings = {
      experience: ["Rookie", "Intermediate", "Pro"],
      "assessment duration": [
        "Up to 10 mins",
        "11–30 mins",
        "30–45 mins",
        "Above 45 mins",
      ],
    };

    // Go through each filter category
    Object.keys(sortedFilters).forEach((category) => {
      // Check if we have a predefined order for this category
      if (orderMappings[category]) {
        // Sort according to our predefined order
        sortedFilters[category] = [...sortedFilters[category]].sort((a, b) => {
          return (
            orderMappings[category].indexOf(a.name) -
            orderMappings[category].indexOf(b.name)
          );
        });
      }
    });

    return sortedFilters;
  };

  // Sort the filters according to our predefined order
  const sortedFilters = sortFilters(filters);
  return (
    <Box height="100%">
      {/* Main Content */}
      <Box
        height="100%"
        sx={{
          display: "flex",
          flexDirection: { md: "row", sm: "column", xs: "column" },
        }}
      >
        <Box height="95%" flex={3} sx={{ pb: 2 }}>
          <GridListView
            data={assessments}
            isLoading={isLoading}
            renderHeader={renderHeader}
            emptyTxt={
              !isUserCompanySetup
                ? "Please setup company to view asessments"
                : "No assessments found for the selected filters."
            }
            useImg2={false}
            containerStyle={{ height: "100%" }}
            onLoadMore={() => {
              _getData(departmentIds, page, selectedFilters, true);
            }} // Correctly passing page
            renderItem={(index, id) => (
              <AssessmentsCards
                index={index}
                key={id}
                id={id}
                handleAssessmentSelect={handleAssessmentSelect}
                handleOpenModal={handleOpenModal}
                isModal={isModal}
                selectedAssessmentId={selectedAssessmentId}
              />
            )}
          />
        </Box>

        {/* Filters Sidebar */}
        <Box
          flex={1}
          p={1}
          sx={{
            my: 2,
            height: "95%",
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
          }}
        >
          {isUserCompanySetup ? (
            <>
              {/* Search Bar */}
              <TextField
                fullWidth
                placeholder="Search assessments here"
                variant="outlined"
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{
                  bgcolor: "white",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E0E0E0",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ height: "70%", overflow: "auto" }}>
                <Box sx={{ p: 2 }}>
                  {sortedFilters && Object.keys(sortedFilters).length > 0 && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                        Filters
                      </Typography>
                      {Object.entries(sortedFilters).map(
                        ([category, options]) => (
                          <Accordion
                            key={category}
                            expanded={expanded.includes(category)}
                            onChange={() => handleAccordionToggle(category)}
                            elevation={0}
                            sx={{
                              "&:before": {
                                display: "none",
                              },
                              borderBottom: "1px solid #E0E0E0",
                              px: 1,
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                px: 0,
                                "& .MuiAccordionSummary-content": {
                                  my: 1,
                                },
                              }}
                            >
                              <Typography sx={{ fontWeight: 500 }}>
                                {getCamelCaseWord(category)}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 0 }}>
                              {options?.map((option) => (
                                <FormControlLabel
                                  key={option.name}
                                  control={
                                    <Checkbox
                                      checked={
                                        selectedFilters[category]?.includes(
                                          option.name
                                        ) || false
                                      }
                                      onChange={() =>
                                        handleCheckboxChange(
                                          category,
                                          option.name
                                        )
                                      }
                                    />
                                  }
                                  label={
                                    <Typography sx={{ fontSize: "0.875rem" }}>
                                      {option.name}{" "}
                                      {option.count && `(${option.count})`}
                                    </Typography>
                                  }
                                  sx={{
                                    display: "flex",
                                    "& .MuiTypography-root": {
                                      fontSize: "0.875rem",
                                    },
                                  }}
                                />
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        )
                      )}
                    </>
                  )}
                </Box>
              </Box>
              {!isModal && (
                <Box
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    background: "white",
                    py: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="main"
                    endIcon={
                      <ArrowForward sx={{ width: "20px", height: "20px" }} />
                    }
                    sx={{ py: 1, pr: 3, ":disabled": { bgcolor: "#999" } }}
                    disabled={!selectedAssessmentId}
                    onClick={() => {
                      if (!isUserCompanySetup) {
                        setActionButtonToggle(true);
                        enqueueSnackbar("Please setup company !", {
                          variant: "info",
                        });
                      } else {
                        setOpenDetailModal(true);
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      Next
                    </Box>
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                height: "95%",
                gap: 2,
              }}
            >
              <Typography sx={{ fontFamily: "Outfit", fontSize: 20 }}>
                Please setup your company first.
              </Typography>
              <img alt="AI Robot" src={AIROBOT} height={120} />
            </Box>
          )}
        </Box>
      </Box>
      {/* Modal Component */}
      <AssessmentDetailsModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setSelectedAssessment(null);
        }}
        assessment={selectedAssessment}
      />

      <InviteViaAssessmentModal
        open={openDetailModal}
        onClose={() => handleClose()}
        interviewValue={interviewValue}
        setInterviewValue={setInterviewValue}
        passingCriteriaSet={passingCriteriaSet}
        setPassingCriteriaSet={setPassingCriteriaSet}
        tabValue={tabValue}
        setTabValue={setTabValue}
        selectedAssessmentId={selectedAssessmentId}
        copied={copied}
        setCopied={setCopied}
        linkGenerated={linkGenerated}
        setLinkGenerated={setLinkGenerated}
        interviewId={interviewId}
        setInterviewId={setInterviewId}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  isLoading: selectLoading(state),
  assessments: selectAssessmentIds(state),
  page: selectPage(state),
  isUserCompanySetup: state.auth?.user?.company_id,
  company: state.company.company,
  filters: state?.assessments?.filter,
});

const mapDispatchToProps = {
  setActionButtonToggle: setActionButtonToggleAction,
  getSingleDepartmentAssessment: getSingleDepartmentAssessmentAction,
  getAllAssessments: getAllAssessmentsAction,
  getFilterData: getFilterDataAction,
  applyAndSearchFilter: applyAndSearchFilterAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(InterviewAssessments));
